import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  CARE_POLICIES, CATEGORIES, CATEGORY_DEFAULTS, LAUNDRY_GROUPS, LAYERS, WASHABLE_POLICIES,
  labelOf, type CarePolicy, type Category, type LaundryGroup, type Layer,
} from '@wardrobe/shared';

/** Nach der deutschen Beschriftung sortiert — so sucht man in einer Auswahlliste. */
const KATEGORIEN = [...CATEGORIES].sort((a, b) =>
  categoryLabel(a).localeCompare(categoryLabel(b), 'de'),
);
import { api } from '../api.js';
import {
  Button, Card, ErrorBox, Loading, Screen, carePolicyLabel, categoryLabel,
  laundryGroupLabel, layerLabel,
} from '../ui.js';

/**
 * §8: Ziel ist unter 20 Sekunden pro Teil. Deshalb: Kategorie wählen, Name tippen,
 * fertig — alles andere füllen die Kategorie-Startwerte vor und lässt sich später
 * korrigieren. Fotos werden sofort hochgeladen, die Analyse läuft im Hintergrund.
 */
export function AddItem({ navigate }: { navigate: (to: string) => void }): ReactNode {
  const qc = useQueryClient();
  // `null` heisst „noch nicht angefasst" — dann darf der Foto-Vorschlag greifen.
  const [name, setName] = useState<string | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [color, setColor] = useState<string | null>(null);
  // Wie Name und Farbe: `null` heisst „noch nicht angefasst", damit der erkannte Wert
  // greifen darf. Als leerer String konnte hier nie ein Vorschlag ankommen.
  const [brand, setBrand] = useState<string | null>(null);
  const [price, setPrice] = useState('');
  const [hint, setHint] = useState<string | null>(null);
  const [csv, setCsv] = useState('');
  const [anzahl, setAnzahl] = useState(1);
  // Pflege und die beiden Zaehler folgen der Kategorie, bis sie angefasst werden.
  const [pflege, setPflege] = useState<CarePolicy | null>(null);
  const [proWaesche, setProWaesche] = useState<number | null>(null);
  const [proReinigung, setProReinigung] = useState<number | null | undefined>(undefined);
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [letzteAnzahl, setLetzteAnzahl] = useState(0);



  const create = useMutation({
    mutationFn: () =>
      api.createItem({
        name: nameWert,
        category: kategorie,
        colorPrimaryName: farbeWert === '' ? null : farbeWert,
        carePolicy: pflegeWert,
        wearsPerWash: waescheWert,
        wearsPerClean: reinigungWert,
        brand: markeWert === '' ? null : markeWert,
        purchasePriceCents: price === '' ? null : Math.round(Number(price.replace(',', '.')) * 100),
        // D-37: alles, was die Analyse erkannt hat — bis hierher blieb davon nur Name,
        // Kategorie und Farbe übrig, der Rest wurde still durch Kategorie-Mittelwerte
        // ersetzt.
        layer: schicht,
        warmthPoints: waerme,
        formality: formalitaet,
        laundryGroup: waeschegruppe,
        needsIroning: buegeln,
        subcategory: ausEntwurf('subcategory'),
        pattern: ausEntwurf('pattern'),
        colorPrimaryHex: ausEntwurf('colorPrimaryHex'),
        ...(waschTemp === null ? {} : { washTempC: waschTemp }),
        ...(materialien === null ? {} : { materials: materialien }),
        // Das Foto gehört an das Teil, das gerade daraus entsteht.
        ...(photoId === null ? {} : { photoId }),
        copies: anzahl,
      }),
    onSuccess: (item) => {
      setHint(item.duplicateHint);
      setLetzteAnzahl(item.copies);
      setName(null);
      setColor(null);
      setCategory(null);
      setPflege(null);
      setProWaesche(null);
      setProReinigung(undefined);
      setBrand(null);
      setPrice('');
      setAnzahl(1);
      setPhotoId(null);
      void qc.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const upload = useMutation({
    mutationFn: (files: File[]) => api.uploadPhotos(files),
    onSuccess: (data) => {
      const erstes = data.photos[0];
      if (erstes === undefined) return;
      setPhotoId(erstes.id);
      // Die Warteschlange sofort anstoßen, statt bis zu zwei Minuten auf den Takt des
      // Hintergrundlaufs zu warten — beim Erfassen steht man daneben und schaut zu.
      if (erstes.jobId !== null) void api.processIngest().catch(() => undefined);
    },
  });

  /**
   * Der Kern von D-32: Was die Bildanalyse erkannt hat, füllt das Formular vor.
   * Bis hierher lief die Analyse zwar, ihr Ergebnis lag aber nur in der Datenbank —
   * kein Bildschirm hat es je gezeigt, weshalb „Gemini, hier ist das Foto" gefühlt
   * gar nicht gebaut war. Übernommen wird nur, was leer ist: Getipptes gewinnt.
   */
  const entwurf = useQuery({
    queryKey: ['photoDraft', photoId],
    queryFn: () => api.photoDraft(photoId as string),
    enabled: photoId !== null,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'pending' || s === 'running' ? 1500 : false;
    },
  });

  // Abgeleitet statt in den State kopiert: der Vorschlag ist Serverzustand und bleibt
  // dort. Getipptes gewinnt — sobald ein Feld angefasst wurde, zählt nur noch das.
  const vorschlag = entwurf.data?.status === 'done' ? entwurf.data.draft : null;
  const ausEntwurf = (schluessel: string): string | null => {
    const wert = vorschlag?.[schluessel];
    return typeof wert === 'string' && wert.trim() !== '' ? wert : null;
  };
  /** Zahl aus dem Entwurf, nur innerhalb des erlaubten Bereichs. */
  const zahlAusEntwurf = (schluessel: string, min: number, max: number): number | null => {
    const wert = vorschlag?.[schluessel];
    return typeof wert === 'number' && Number.isFinite(wert) && wert >= min && wert <= max
      ? Math.round(wert)
      : null;
  };
  /** Aufzählungswert aus dem Entwurf — was der Katalog nicht kennt, wird verworfen. */
  const wahlAusEntwurf = <T extends string>(schluessel: string, erlaubt: readonly T[]): T | null => {
    const wert = vorschlag?.[schluessel];
    return typeof wert === 'string' && (erlaubt as readonly string[]).includes(wert)
      ? (wert as T)
      : null;
  };
  const jaNeinAusEntwurf = (schluessel: string): boolean | null => {
    const wert = vorschlag?.[schluessel];
    return typeof wert === 'boolean' ? wert : null;
  };

  const vorgeschlageneKategorie = wahlAusEntwurf('category', CATEGORIES);
  const kategorie: Category = category ?? vorgeschlageneKategorie ?? 'shirt';
  const nameWert = name ?? ausEntwurf('name') ?? '';
  const farbeWert = color ?? ausEntwurf('colorPrimaryName') ?? '';
  const markeWert = brand ?? ausEntwurf('brand') ?? '';

  /*
   * D-38: Die Startwerte kommen vom Server, weil sie dort vom eigenen Bestand überlagert
   * werden. Solange sie nicht geladen sind, gilt die Kategorie-Tabelle — das Formular
   * soll ohne Netz nicht leer dastehen.
   */
  const startwerte = useQuery({ queryKey: ['defaults'], queryFn: api.defaults });
  const defaults = startwerte.data?.[kategorie] ?? {
    ...CATEGORY_DEFAULTS[kategorie],
    quellen: {},
  };
  const QUELLE_TEXT: Record<string, string> = {
    katalog: 'aus der Kategorie',
    bestand: 'aus deinem Bestand',
    gesetzt: 'von dir gesetzt',
  };
  const quelle = (feld: string, ausFoto: boolean): string =>
    ausFoto ? 'aus dem Foto' : (QUELLE_TEXT[defaults.quellen[feld] ?? 'katalog'] ?? 'aus der Kategorie');
  const erkanntePflege = wahlAusEntwurf('carePolicy', CARE_POLICIES);
  const pflegeWert: CarePolicy = pflege ?? erkanntePflege ?? defaults.carePolicy;
  const waschbar = (WASHABLE_POLICIES as readonly string[]).includes(pflegeWert);
  const waescheWert = proWaesche ?? defaults.wearsPerWash;
  // `undefined` heisst „noch nicht angefasst", `null` heisst ausdruecklich „nur bei Bedarf".
  const reinigungWert = proReinigung === undefined ? defaults.wearsPerClean : proReinigung;

  /*
   * D-37: Diese Felder haben kein eigenes Eingabefeld — sie würden das Ziel „unter 20
   * Sekunden pro Teil" zerreden. Verworfen wurden sie bis hierher trotzdem nicht zu
   * Recht: die Analyse hat sie geliefert, und ein erkannter Wert ist besser als der
   * Durchschnitt einer Kategorie. Erkanntes gewinnt gegen den Startwert, und die
   * Zusammenfassung unten sagt, was gespeichert wird und woher es stammt.
   */
  const erkannteSchicht = wahlAusEntwurf('layer', LAYERS);
  const erkannteWaerme = zahlAusEntwurf('warmthPoints', 0, 10);
  const erkannteFormalitaet = zahlAusEntwurf('formality', 1, 5);
  const erkannteGruppe = wahlAusEntwurf('laundryGroup', LAUNDRY_GROUPS);
  const erkanntesBuegeln = jaNeinAusEntwurf('needsIroning');
  // Nur vom Pflegeetikett und nur bei sicherer Erkennung — sonst kommt hier nichts an.
  const waschTemp = zahlAusEntwurf('washTempC', 0, 95);

  const schicht: Layer = erkannteSchicht ?? defaults.layer;
  const waerme = erkannteWaerme ?? defaults.warmthPoints;
  const formalitaet = erkannteFormalitaet ?? defaults.formality;
  const waeschegruppe: LaundryGroup = erkannteGruppe ?? defaults.laundryGroup;
  const buegeln = erkanntesBuegeln ?? defaults.needsIroning;
  const materialien = Array.isArray(vorschlag?.['materials']) ? vorschlag['materials'] : null;

  /** Was gespeichert wird und woher es kommt — eine Liste für Meldung und Übersicht. */
  const herkunft: { feld: string; schluessel: string; wert: string; ausFoto: boolean }[] = [
    { feld: 'Schicht', schluessel: 'layer', wert: layerLabel(schicht), ausFoto: erkannteSchicht !== null },
    { feld: 'Wärme', schluessel: 'warmthPoints', wert: `${waerme}/10`, ausFoto: erkannteWaerme !== null },
    { feld: 'Formalität', schluessel: 'formality', wert: `${formalitaet}/5`, ausFoto: erkannteFormalitaet !== null },
    { feld: 'Wäschegruppe', schluessel: 'laundryGroup', wert: laundryGroupLabel(waeschegruppe), ausFoto: erkannteGruppe !== null },
    { feld: 'Bügeln', schluessel: 'needsIroning', wert: buegeln ? 'ja' : 'nein', ausFoto: erkanntesBuegeln !== null },
    { feld: 'Trockenzeit', schluessel: 'dryingHours', wert: `${defaults.dryingHours} h`, ausFoto: false },
    ...(waschTemp === null
      ? []
      : [{ feld: 'Waschtemperatur', schluessel: 'washTempC', wert: `${waschTemp} °C`, ausFoto: true }]),
  ];

  const uebernommen =
    vorschlag === null
      ? null
      : [
          category === null && vorgeschlageneKategorie !== null ? 'Kategorie' : null,
          name === null && ausEntwurf('name') !== null ? 'Name' : null,
          color === null && ausEntwurf('colorPrimaryName') !== null ? 'Farbe' : null,
          brand === null && ausEntwurf('brand') !== null ? 'Marke' : null,
          pflege === null && erkanntePflege !== null ? 'Pflege' : null,
          ...herkunft.filter((h) => h.ausFoto).map((h) => h.feld),
          ausEntwurf('pattern') !== null ? 'Muster' : null,
          materialien !== null ? 'Material' : null,
        ].filter((f): f is string => f !== null);

  const importCsv = useMutation({
    mutationFn: () => api.importCsv(csv),
    onSuccess: () => {
      setCsv('');
      void qc.invalidateQueries({ queryKey: ['items'] });
    },
  });

  return (
    <Screen
      title="Teil erfassen"
      action={<Button variant="ghost" onClick={() => navigate('/schrank')}>Fertig</Button>}
    >
      {/* Steht bewusst ganz oben: Der schnellste Weg ist Foto zuerst, Rest korrigieren. */}
      <Card className="mb-4">
        <p className="text-sm font-medium">Mit einem Foto anfangen</p>
        <p className="mt-1 text-xs text-white/55">
          Bild aufnehmen — Kategorie, Name, Marke, Farbe, Schicht, Wärme, Formalität und
          Pflege werden daraus vorgeschlagen und sind frei überschreibbar. EXIF inklusive
          Standort wird beim Speichern entfernt.
        </p>
        {/* D-37: Der Mehrfach-Upload gab es schon, benutzt wurde nur das erste Bild. */}
        <p className="mt-1 text-xs text-white/40">
          Zwei Bilder auf einmal: erst das Teil, dann sein Pflegeetikett. Waschtemperatur und
          Bügeln werden dann abgelesen statt geschätzt.
        </p>
        <label className="mt-3 block">
          <span className="sr-only">Foto auswählen</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) upload.mutate(files);
            }}
            className="peer sr-only"
          />
          {/* Der native Knopf traegt die Sprache des Browsers ("Choose Files"). */}
          <span className="block w-full cursor-pointer rounded-[var(--radius-control)] bg-white/10 px-4 py-3 text-center text-[15px] text-white/85 peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-accent)]">
            {upload.isPending ? 'Wird hochgeladen …' : 'Foto auswählen oder aufnehmen'}
          </span>
        </label>
        <ErrorBox error={upload.error} />

        {/*
          Kein Auftrag heisst: niemand wertet dieses Foto aus. Bis hierher nannte die
          Meldung dafuer immer den fehlenden Schluessel — auch wenn schlicht der Schalter
          aus war. Der Server sagt jetzt, welcher der beiden Faelle vorliegt.
        */}
        {upload.data !== undefined && upload.data.photos.every((p) => p.jobId === null) && (
          <p className="mt-2 text-xs text-[#ffc266]">
            Foto gespeichert.{' '}
            {upload.data.analyse.reason === 'kein_schluessel'
              ? 'Die automatische Erkennung ist eingeschaltet, aber im Stack fehlt der GEMINI_API_KEY — ohne ihn kann sie nicht laufen.'
              : 'Die automatische Erkennung ist ausgeschaltet — einschalten unter „Mehr“.'}
          </p>
        )}

        {photoId !== null && entwurf.data !== undefined && (
          <div className="mt-3 text-xs">
            {(entwurf.data.status === 'pending' || entwurf.data.status === 'running') && (
              <>
                <Loading label="Foto wird ausgewertet …" />
                {/*
                  Ein gescheiterter Versuch, der gleich wiederholt wird, ist kein Grund
                  zu schweigen: vorher drehte sich hier dieselbe Anzeige, ob das Modell
                  nun nachdachte oder der Pi seit Minuten nicht nach draussen kam.
                */}
                {entwurf.data.error !== null && (
                  <p className="mt-1 text-[#ffc266]">
                    Versuch {entwurf.data.attempts} von {entwurf.data.maxAttempts} ist
                    gescheitert: {entwurf.data.error}. Wird erneut versucht.
                  </p>
                )}
              </>
            )}
            {entwurf.data.status === 'failed' && (
              <p className="text-[#ffc266]">
                Die Auswertung ist gescheitert{entwurf.data.error === null ? '' : `: ${entwurf.data.error}`}.
                Die Felder lassen sich von Hand ausfüllen.
              </p>
            )}
            {entwurf.data.status === 'done' && uebernommen !== null && (
              <>
                <p className="text-[#5ee27f]">
                  {uebernommen.length === 0
                    ? 'Ausgewertet — es blieb nichts zu ergänzen.'
                    : `Aus dem Foto übernommen: ${uebernommen.join(', ')}.`}
                </p>
                {entwurf.data.lowConfidence.length > 0 && (
                  <p className="mt-1 text-white/45">
                    Unsicher war die Erkennung bei:{' '}
                    {entwurf.data.lowConfidence.map((f) => labelOf.analysisField(f)).join(', ')} —
                    bitte nachsehen.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </Card>

      <Card className="mb-4">
        <label className="block text-xs text-white/55">Kategorie</label>
        <select
          value={kategorie}
          onChange={(e) => setCategory(e.target.value as Category)}
          className="mt-1 w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
        >
          {/* Alphabetisch nach der deutschen Beschriftung — im Katalog stehen sie in
              fachlicher, nicht in auffindbarer Reihenfolge. */}
          {KATEGORIEN.map((c) => (
            <option key={c} value={c}>
              {categoryLabel(c)}
            </option>
          ))}
        </select>

        <label className="mt-4 block text-xs text-white/55">Name</label>
        <input
          value={nameWert}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Weißes Oxford-Hemd"
          className="mt-1 w-full rounded-xl bg-white/6 px-3 py-3 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
        />

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-white/55">Farbe</label>
            <input
              value={farbeWert}
              onChange={(e) => setColor(e.target.value)}
              placeholder="Weiß"
              className="mt-1 w-full rounded-xl bg-white/6 px-3 py-3 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
            />
          </div>
          <div>
            <label className="block text-xs text-white/55">Marke</label>
            <input
              value={markeWert}
              onChange={(e) => setBrand(e.target.value)}
              className="mt-1 w-full rounded-xl bg-white/6 px-3 py-3 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
            />
          </div>
        </div>

        {/*
          Bis hierher waren Pflege und die beiden Zähler nur Startwerte der Kategorie und
          im Formular gar nicht erreichbar. Genau das fehlte: „geht nur in die Reinigung"
          ist eine Eigenschaft des einzelnen Stücks, nicht seiner Kategorie.
        */}
        <label className="mt-4 block text-xs text-white/55">Pflege</label>
        <select
          value={pflegeWert}
          onChange={(e) => setPflege(e.target.value as CarePolicy)}
          className="mt-1 w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
        >
          {CARE_POLICIES.map((p) => (
            <option key={p} value={p}>
              {carePolicyLabel(p)}
            </option>
          ))}
        </select>
        {!waschbar && (
          <p className="mt-1 text-xs text-white/45">
            Wird beim Tragen nie automatisch „schmutzig" und landet nicht im Wäschekorb.
            In die Reinigung geht es, wenn du es dorthin gibst — auf dem Wäsche-Bildschirm.
          </p>
        )}

        {waschbar && (
          <>
            <label className="mt-4 block text-xs text-white/55">
              Wäsche nach wie vielen Tragungen?
            </label>
            <input
              value={waescheWert}
              inputMode="numeric"
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                setProWaesche(Number.isNaN(n) ? 1 : Math.min(100, Math.max(1, n)));
              }}
              className="tnum mt-1 w-full rounded-xl bg-white/6 px-3 py-3 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
            />
          </>
        )}

        <label className="mt-4 block text-xs text-white/55">Reinigung</label>
        <div className="mt-1 flex gap-2">
          <button
            onClick={() => setProReinigung(null)}
            className={`flex-1 rounded-full px-3 py-2 text-sm ${
              reinigungWert === null ? 'bg-white text-black' : 'bg-white/8 text-white/75'
            }`}
          >
            Nur bei Bedarf
          </button>
          <button
            onClick={() => setProReinigung(reinigungWert ?? defaults.wearsPerClean ?? 15)}
            className={`flex-1 rounded-full px-3 py-2 text-sm ${
              reinigungWert !== null ? 'bg-white text-black' : 'bg-white/8 text-white/75'
            }`}
          >
            Nach Tragungen
          </button>
        </div>
        {reinigungWert === null ? (
          <p className="mt-1 text-xs text-white/45">
            Kein Zähler. Das Teil meldet sich nie von selbst — du gibst es in die Reinigung,
            wenn es wirklich schmutzig ist.
          </p>
        ) : (
          <>
            <input
              value={reinigungWert}
              inputMode="numeric"
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                setProReinigung(Number.isNaN(n) ? 1 : Math.min(200, Math.max(1, n)));
              }}
              className="tnum mt-2 w-full rounded-xl bg-white/6 px-3 py-3 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
            />
            <p className="mt-1 text-xs text-white/45">
              Nach {reinigungWert} Tragungen erscheint ein Hinweis — er blockiert nichts und
              zählt nur Tragungen, keine Tage.
            </p>
          </>
        )}

        <label className="mt-4 block text-xs text-white/55">Kaufpreis (€, optional)</label>
        <input
          value={price}
          inputMode="decimal"
          onChange={(e) => setPrice(e.target.value)}
          placeholder="89,00"
          className="mt-1 w-full rounded-xl bg-white/6 px-3 py-3 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
        />

        <div className="mt-4 rounded-lg bg-black/20 p-3 text-xs text-white/55">
          <p className="mb-2">Wird so gespeichert:</p>
          <ul className="space-y-1">
            {herkunft.map((h) => (
              <li key={h.feld} className="flex justify-between gap-3">
                <span>{h.feld}</span>
                <span className="text-white/80">
                  {h.wert}
                  <span className="ml-2 text-[10px] text-white/35">
                    {quelle(h.schluessel, h.ausFoto)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-white/35">
            {Object.values(defaults.quellen).some((q) => q !== 'katalog')
              ? 'Was „aus deinem Bestand" stammt, ist der Mittelwert deiner eigenen Teile dieser Kategorie — die App hat ihn beim Waschen und Trocknen gelernt. Was „von dir gesetzt" ist, steht unter „Mehr → Kategorie-Startwerte". Alles andere sind pragmatische Schätzungen.'
              : 'Kategorie-Startwerte sind pragmatische Schätzungen, keine gemessenen Werte — am Teil jederzeit änderbar.'}
          </p>
        </div>

        {/* Fünf gleiche Hemden sind fünf Teile — jedes hat seinen eigenen Waschzustand.
            Hier ist es einmal Tippen statt fünfmal. */}
        <label className="mt-4 block text-xs text-white/55">Anzahl gleicher Teile</label>
        <input
          value={anzahl}
          inputMode="numeric"
          onChange={(e) => {
            const n = Number.parseInt(e.target.value, 10);
            setAnzahl(Number.isNaN(n) ? 1 : Math.min(20, Math.max(1, n)));
          }}
          className="tnum mt-1 w-full rounded-xl bg-white/6 px-3 py-3 text-sm ring-1 ring-white/10 outline-none focus:ring-sky-500"
        />
        {anzahl > 1 && (
          <p className="mt-1 text-xs text-white/45">
            Legt {anzahl} eigenständige Teile an — „{nameWert.trim() === '' ? 'Name' : nameWert.trim()} 1"
            bis „{nameWert.trim() === '' ? 'Name' : nameWert.trim()} {anzahl}". Jedes hat seinen eigenen
            Waschzustand, sonst ließe sich „drei sauber, zwei im Korb" nicht abbilden.
          </p>
        )}

        <Button
          className="mt-4 w-full"
          onClick={() => create.mutate()}
          disabled={nameWert.trim() === '' || create.isPending}
        >
          {create.isPending ? 'Speichert …' : 'Anlegen und nächstes'}
        </Button>
        <ErrorBox error={create.error} />
        {create.isSuccess && (
          <p className="mt-3 text-sm text-[#5ee27f]">
            {letzteAnzahl > 1 ? `${letzteAnzahl} Teile angelegt.` : 'Angelegt.'}
          </p>
        )}
        {hint !== null && (
          <p className="mt-2 rounded-lg bg-[#ff9f0a]/12 p-2 text-xs text-[#ffc266]">{hint}</p>
        )}
      </Card>

      <Card>
        <p className="text-sm font-medium">CSV-Import</p>
        <p className="mt-1 text-xs text-white/55">
          Kopfzeile mit Spaltennamen, z. B. <code>name,category,colorPrimaryName</code>.
          Fehlerhafte Zeilen werden gemeldet, der Rest wird importiert.
        </p>
        <textarea
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={4}
          placeholder="name,category,colorPrimaryName&#10;Blaues Hemd,shirt,Blau"
          className="mt-3 w-full rounded-xl bg-white/6 px-3 py-2 font-mono text-xs ring-1 ring-white/10 outline-none focus:ring-sky-500"
        />
        <Button
          variant="ghost"
          className="mt-3 w-full"
          onClick={() => importCsv.mutate()}
          disabled={csv.trim() === '' || importCsv.isPending}
        >
          Importieren
        </Button>
        <ErrorBox error={importCsv.error} />
        {importCsv.data !== undefined && (
          <div className="mt-2 text-sm">
            <p className="text-[#5ee27f]">{importCsv.data.created} Teile importiert.</p>
            {importCsv.data.failed.map((f) => (
              <p key={f.row} className="text-[#ffc266]">
                Zeile {f.row}: {f.message}
              </p>
            ))}
          </div>
        )}
      </Card>
    </Screen>
  );
}
